import { useCallback, useEffect, useState } from 'react';
import { GraduationCap, Lock, Sparkles, ChevronDown, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { apiClient } from '../lib/api-client';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs, PillTabsList, PillTabsTrigger } from '../components/ui/PillTabs';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { LearnedEntriesTab } from '../components/ai-workflows/LearnedEntriesTab';
import { InsightsTab } from '../components/ai-workflows/InsightsTab';
import { ProposalsTab } from '../components/ai-workflows/ProposalsTab';
import { HistoryTab } from '../components/ai-workflows/HistoryTab';
import { TeachDialog, AiStreamOption } from '../components/ai-workflows/TeachDialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { useSearchParams } from 'react-router-dom';
import { setActiveAiWorkspaceId } from '../lib/api-client';

type LearningTab = 'entries' | 'insights' | 'proposals' | 'history';
type WorkspaceSummary = { id: string; displayName: string };

function mapStreams(raw: unknown[]): AiStreamOption[] {
  return raw.map((s) => {
    const o = s as Record<string, unknown>;
    return {
      streamId: String(o.streamId ?? ''),
      displayName: String(o.displayName ?? o.streamId ?? ''),
    };
  }).filter((s) => s.streamId);
}

export function AiLearning() {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspace') || 'maxiron';
  const { user } = useAuth();
  const { isAllowed: canRead, isLoading: readPermLoading } = usePermission('ai_workflows', 'read');
  const { isAllowed: canManage } = usePermission('ai_workflows', 'manage');

  const hasNoGroups = Boolean(user && (!user.groups || user.groups.length === 0));
  if (hasNoGroups) return <NoGroupsMessage />;

  const [streams, setStreams] = useState<AiStreamOption[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [streamsError, setStreamsError] = useState<string | null>(null);
  const [selectedStream, setSelectedStream] = useState('');
  const [activeTab, setActiveTab] = useState<LearningTab>('entries');
  const [entryTotal, setEntryTotal] = useState(0);
  const [insightCount, setInsightCount] = useState<number | null>(null);
  const [proposalCount, setProposalCount] = useState<number | null>(null);
  const [historyCount, setHistoryCount] = useState<number | null>(null);
  const [teachOpen, setTeachOpen] = useState(false);

  const loadStreams = useCallback(async () => {
    setActiveAiWorkspaceId(workspaceId);
    if (!canRead) {
      setStreamsLoading(false);
      return;
    }
    setStreamsLoading(true);
    setStreamsError(null);
    try {
      const res = await apiClient.getAiWorkflowStreams();
      const list = mapStreams(Array.isArray(res.streams) ? res.streams : []);
      setStreams(list);
      setSelectedStream((prev) => {
        if (prev && list.some((s) => s.streamId === prev)) return prev;
        return list[0]?.streamId || '';
      });
    } catch (e: unknown) {
      setStreamsError(e instanceof Error ? e.message : 'Failed to load streams');
      setStreams([]);
      setSelectedStream('');
    } finally {
      setStreamsLoading(false);
    }
  }, [canRead, workspaceId]);

  useEffect(() => {
    void loadStreams();
  }, [loadStreams]);

  useEffect(() => {
    if (!canRead) return;
    void apiClient.getAiWorkspaces()
      .then((res) => {
        setWorkspaces((res.workspaces || []).map((w: any) => ({ id: w.id, displayName: w.displayName || w.id })));
      })
      .catch(() => setWorkspaces([]));
  }, [canRead]);

  const refreshEntryTotal = useCallback(async () => {
    if (!selectedStream || !canRead) return;
    try {
      const res = await apiClient.getAiWorkflowKnowledge(selectedStream);
      setEntryTotal(typeof res.total === 'number' ? res.total : (res.entries?.length ?? 0));
    } catch {
      setEntryTotal(0);
    }
  }, [selectedStream, canRead]);

  useEffect(() => {
    void refreshEntryTotal();
  }, [refreshEntryTotal]);

  const prefetchTabCounts = useCallback(async () => {
    if (!selectedStream || !canRead) return;
    setInsightCount(null);
    setProposalCount(null);
    setHistoryCount(null);
    try {
      const [ins, prop, act] = await Promise.all([
        apiClient.getAiWorkflowEvolutionInsights(selectedStream),
        apiClient.getAiWorkflowEvolutionProposals(selectedStream),
        apiClient.getAiWorkflowActivity({ streamId: selectedStream, limit: 200 }),
      ]);
      setInsightCount(Array.isArray(ins.insights) ? ins.insights.length : 0);
      setProposalCount(Array.isArray(prop.proposals) ? prop.proposals.length : 0);
      setHistoryCount(Array.isArray(act.entries) ? act.entries.length : 0);
    } catch {
      setInsightCount(0);
      setProposalCount(0);
      setHistoryCount(0);
    }
  }, [selectedStream, canRead]);

  useEffect(() => {
    void prefetchTabCounts();
  }, [prefetchTabCounts]);

  const handleTeachSubmitted = useCallback(() => {
    void refreshEntryTotal();
  }, [refreshEntryTotal]);

  if (readPermLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <Lock className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">You do not have access to AI Learning.</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
              Ask an administrator for the <span className="font-mono">ai_workflows</span> read permission.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={GraduationCap}
        iconColor="text-emerald-600 dark:text-emerald-400"
        iconBgColor="bg-emerald-100 dark:bg-emerald-900/30"
        description={`AI Learning (${workspaceId})`}
        count={entryTotal}
        countLabel="entries"
        countColor="text-emerald-600 dark:text-emerald-400"
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-stretch gap-2 w-full sm:w-auto h-full">
            <DropdownMenu
              align="end"
              className="h-full"
              trigger={(
                <Button variant="outline" className="h-full justify-between gap-2 min-w-[9rem] w-full">
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate text-left">
                    {(workspaces.length ? workspaces : [{ id: workspaceId, displayName: workspaceId }])
                      .find((w) => w.id === workspaceId)?.displayName || workspaceId}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0" />
                </Button>
              )}
              items={(workspaces.length ? workspaces : [{ id: workspaceId, displayName: workspaceId }]).map((w) => ({
                label: w.displayName,
                onClick: () => {
                  setSearchParams((prev) => {
                    const p = new URLSearchParams(prev);
                    p.set('workspace', w.id);
                    return p;
                  }, { replace: true });
                  setActiveAiWorkspaceId(w.id);
                },
              }))}
            />
            {streamsLoading ? (
              <Skeleton className="h-full w-40 rounded-md" />
            ) : streamsError ? (
              <span className="self-center text-xs text-red-600 dark:text-red-400">{streamsError}</span>
            ) : (
              <DropdownMenu
                align="end"
                className="h-full"
                trigger={(
                  <Button variant="outline" className="h-full justify-between gap-2 min-w-[9rem] w-full" disabled={streams.length === 0}>
                    <span className="truncate text-left">
                      {streams.find((s) => s.streamId === selectedStream)?.displayName
                        || (streams.length === 0 ? 'No streams' : 'Select stream')}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </Button>
                )}
                items={streams.map((s) => ({
                  label: s.displayName || s.streamId,
                  onClick: () => setSelectedStream(s.streamId),
                }))}
              />
            )}
            <Button
              className="h-full"
              onClick={() => setTeachOpen(true)}
              disabled={!canManage || streams.length === 0}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Teach AI
            </Button>
          </div>
        }
      />

      {!streamsLoading && streams.length === 0 && !streamsError && (
        <Card>
          <CardContent className="py-8 text-center text-gray-600 dark:text-gray-400 text-sm">
            No AI streams are configured. Add stream configuration in object storage to see data here.
          </CardContent>
        </Card>
      )}

      <PillTabs value={activeTab} onValueChange={(v) => setActiveTab(v as LearningTab)}>
        <PillTabsList>
          <PillTabsTrigger value="entries">
            Learned Entries
            <Badge variant="outline" className="ml-2">
              {entryTotal}
            </Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="insights">
            Insights
            <Badge variant="outline" className="ml-2">
              {insightCount === null ? '…' : insightCount}
            </Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="proposals">
            Proposals
            <Badge variant="outline" className="ml-2">
              {proposalCount === null ? '…' : proposalCount}
            </Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="history">
            History
            <Badge variant="outline" className="ml-2">
              {historyCount === null ? '…' : historyCount}
            </Badge>
          </PillTabsTrigger>
        </PillTabsList>
      </PillTabs>

      <div className="mt-4">
        {!selectedStream ? (
          <Card>
            <CardContent className="py-10 text-center text-gray-500 dark:text-gray-400 text-sm">
              Select a stream to view AI learning data.
            </CardContent>
          </Card>
        ) : (
          <>
            {activeTab === 'entries' && (
              <LearnedEntriesTab
                streamId={selectedStream}
                canManage={canManage}
                onTotalChange={setEntryTotal}
              />
            )}
            {activeTab === 'insights' && <InsightsTab streamId={selectedStream} />}
            {activeTab === 'proposals' && (
              <ProposalsTab streamId={selectedStream} canManage={canManage} />
            )}
            {activeTab === 'history' && <HistoryTab streamId={selectedStream} />}
          </>
        )}
      </div>

      <TeachDialog
        open={teachOpen}
        onOpenChange={setTeachOpen}
        streams={streams}
        defaultStreamId={selectedStream}
        onSubmitted={handleTeachSubmitted}
      />
    </div>
  );
}
